import Heading from 'LuxKit/Typography/Heading'
import LineClockWithTickIcon from 'LuxKit/Icons/line/LineClockWithTickIcon'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LayoutContainer from 'components/Common/LayoutContainer'
import Image from 'components/Common/Image'
import type { ImageMediaSource } from 'components/Common/Image/Image'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLEStaff } from 'selectors/accountSelectors'
import { rem } from 'polished'
import type { ReactNode } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { breakpointSizes, mediaQueryOnly, mediaQueryUp } from 'components/utils/breakpoint'
import LoyaltyCreditCardNav from './LoyaltyCreditCardNav'
import LoyaltyCreditCardApplyNowButton from './components/LoyaltyCreditCardApplyNowButton'
import { LOYALTY_CREDIT_CARD_APPLY_NOW_PLACEMENTS } from 'analytics/snowplow/events/loyaltyCreditCardEvents'
import {
  LOYALTY_CREDIT_CARD_COLOUR_CREAM,
  LOYALTY_CREDIT_CARD_COLOUR_MINT,
  LOYALTY_CREDIT_CARD_HERO_IMAGE_ID,
  LOYALTY_CREDIT_CARD_LAYOUT_CONTAINER_CLASS,
} from './loyaltyCreditCardConstants'

/* The desktop layer reaches 2884 CSS px on a 5K display and therefore requires
   5768 source px at DPR 2. This source prevents desktop upscaling while the
   default image retains its lighter 4096 px request below the desktop breakpoint. */
const HERO_IMAGE_SOURCES: Array<ImageMediaSource> = [
  {
    media: `(min-width: ${breakpointSizes.desktop.min}px)`,
    id: LOYALTY_CREDIT_CARD_HERO_IMAGE_ID,
    width: 2884,
    dpr: 2,
  },
]

/**
 * The prize roundels have to sit on the card, which lives inside the background
 * photo and has no element of its own. These variables describe where the photo
 * layer is and how big it is, so the image rule and the roundels can be derived
 * from one source instead of being tuned separately and drifting apart whenever
 * the crop changes.
 */
const HeroWrapper = styled.div`
  position: relative;
  overflow: hidden;

  ${mediaQueryOnly.tablet} {
    min-height: ${rem(598)};
  }

  ${mediaQueryUp.desktop} {
    height: ${rem(598)};
    --hero-image-inline-start: min(calc(50% - ${rem(1049)}), 0px);
    --hero-image-block-start: ${rem(-438)};
    --hero-image-height: ${rem(1250)};
    /* The artwork is 1.891:1, so width follows from the height above. */
    --hero-image-width: ${rem(2364)};
  }

  /* Past this width the artwork scales with the viewport, so the frame has to
     scale with it — held at 598px the growing card pushes its roundels out
     through the bottom edge. 28.5vw is 598px at the crossover, so the frame
     height does not jump. */
  @media (min-width: 2098px) {
    height: 28.5vw;
    --hero-image-inline-start: 0px;
    --hero-image-block-start: -20.9vw;
    --hero-image-height: 59.58vw;
    --hero-image-width: 112.66vw;
  }
`

const HeroBackground = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;

  picture,
  > * {
    width: 100%;
    height: 100%;
  }

  /* The hero artwork is already composed as a banner — card centred, sand along
     the bottom — so it fills the frame with plain object-fit rather than the
     oversized, hand-offset layer the earlier crop needed. Nothing here uses an
     inset or a transform, so the layer cannot drift in right-to-left languages:
     a logical inset flips but a translate does not, and mixing the two threw the
     old layer off-centre in Arabic.

     Below desktop the frame is tall and narrow, so cover crops the sides
     heavily; the card sits just right of centre in the artwork, and holding the
     horizontal position there keeps it in view instead of letting it slide off. */
  /* Below desktop the copy stacks above the card, so the layer is taller than
     the frame and anchored to the top: the card then falls into the padded space
     the content reserves beneath the CTA instead of colliding with it. The card
     sits just right of the artwork's centre, and object-position is physical
     rather than logical, so the photo holds its framing in every language. */
  img {
    width: 100%;
    height: 124%;
    object-fit: cover;
    object-position: 54% 0%;
  }

  /* On desktop the layer is sized in absolute units rather than a percentage of
     the frame, so the card is the same size on a laptop as on a 27" monitor. A
     percentage width ties the card to the viewport instead, which reads too
     small on a laptop and too heavy on a wide screen.

     The inline offset is clamped at 0 so the layer can never leave a gap on the
     leading edge; past that width min-width takes over and cover crops. */
  ${mediaQueryUp.desktop} {
    img {
      position: absolute;
      top: var(--hero-image-block-start);
      inset-inline-start: var(--hero-image-inline-start);
      width: auto;
      height: var(--hero-image-height);
      min-width: 100%;
      margin: 0;
      object-position: center;
    }
  }

  /* The copy occupies the left half of the hero grid at every width, so the card
     has to stay clear of the halfway line. The offset above does that by
     tracking the frame centre, but it bottoms out at 0 around 2098px — past
     there the layer stops following the copy and the card slides back under the
     heading. Scaling the layer with the viewport instead holds the same
     relationship at any width. 59.58vw and -20.9vw are the height and offset
     that equal 1250px and -438px at the crossover, so nothing jumps. */
  @media (min-width: 2098px) {
    img {
      top: -20.9vw;
      height: 59.58vw;
    }
  }
`

/**
 * On mobile/tablet the nav is position:relative (in flow), so HeroContent just
 * stacks below it with modest padding. On desktop the nav is position:absolute
 * (overlaid), so padding-top must clear the nav height (~68px).
 */
const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  box-sizing: border-box;
  /* Large bottom padding reserves the card-in-sand area below the CTA on
     mobile/tablet, mirroring the Figma mobile hero. */
  padding: ${rem(32)} 0 ${rem(300)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(40)} ${rem(32)} ${rem(300)};
  }

  ${mediaQueryUp.desktop} {
    padding: ${rem(88)} ${rem(60)} ${rem(72)};
  }
`

/**
 * Single column (stacked) on mobile/tablet — mirrors the nav being in flow.
 * Two columns (side-by-side) on desktop — mirrors the nav being absolute.
 */
const HeroLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(32)};

  ${mediaQueryUp.desktop} {
    grid-template-columns: 1fr 1fr;
    gap: ${rem(60)};
    align-items: center;
    min-height: ${rem(520)};
  }
`

const HeroContentInner = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(24)};
  text-align: center;

  &::before {
    content: '';
    position: absolute;
    inset: ${rem(-56)} ${rem(-80)};
    z-index: 0;
    pointer-events: none;
    background: radial-gradient(ellipse at center, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.65) 38%, rgba(255, 255, 255, 0) 72%);
  }

  > * {
    position: relative;
    z-index: 1;
  }
`

const IntroducingBadge = styled.span`
  display: inline-block;
  background: ${LOYALTY_CREDIT_CARD_COLOUR_CREAM};
  border: 2px solid ${props => props.theme.palette.highlight.primary.darkForeground};
  border-radius: ${rem(100)};
  padding: ${rem(8)} ${rem(16)};
  color: ${props => props.theme.palette.highlight.primary.darkForeground};
  font-size: ${rem(14)};
  font-weight: 600;
  line-height: ${rem(20)};
  white-space: nowrap;
`

const HeroApplyButton = styled(LoyaltyCreditCardApplyNowButton)`
  && {
    min-width: ${rem(148)};
  }
`

const BulletList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(8)};
  width: fit-content;
  max-width: ${rem(290)};
`

const BulletItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${rem(8)};
  font-size: ${rem(16)};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  text-align: start;
`

const BulletIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
`

const HeroCardColumn = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
`

/* Placed against the photo layer rather than the frame, because the roundels
   have to land on the card and the card moves and resizes with the layer. The
   fractions are the card's lower-left corner within the artwork; anchoring to
   the frame centre instead left them floating over bare sand on wide screens.
   Desktop-only — the mobile design uses different copy and placement. */
const WinBadge = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: flex;
    align-items: flex-start;
    position: absolute;
    top: calc(var(--hero-image-block-start) + 0.6528 * var(--hero-image-height));
    inset-inline-start: calc(var(--hero-image-inline-start) + 0.4586 * var(--hero-image-width));
    z-index: 1;
  }
`

/* Circles size from their content with the Figma dimensions as minimums, so
   English keeps the exact design size while longer locales grow to fit. */
const WinBadgeCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 50%;
  aspect-ratio: 1 / 1;
  width: fit-content;
  text-align: center;
  font-size: ${rem(18)};
  line-height: 1.15;
  letter-spacing: ${rem(-0.65)};
  font-weight: ${props => props.theme.font.primary.weight.regular};
`

const WinBadgeMaldivesCircle = styled(WinBadgeCircle)`
  min-width: ${rem(146)};
  min-height: ${rem(146)};
  padding-block: ${rem(12)} ${rem(8)};
  padding-inline: ${rem(10)} ${rem(8)};
  background: ${LOYALTY_CREDIT_CARD_COLOUR_MINT};
  color: ${props => props.theme.palette.highlight.primary.normalContrast};
  z-index: 1;
`

const WinBadgePointsCircle = styled(WinBadgeCircle)`
  margin-block-start: ${rem(38)};
  margin-inline-start: -${rem(26)};
  min-width: ${rem(136)};
  min-height: ${rem(136)};
  padding: ${rem(8)};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  color: ${props => props.theme.palette.highlight.primary.darkForeground};

  /* Longer locales grow the circle and need the copy nudged clear of the mint
     circle's overlap; English keeps the exact Figma layout. */
  &:not(:lang(en)) {
    padding-inline-start: ${rem(30)};
  }
`

const WinBadgeEmphasis = styled.span`
  font-weight: ${props => props.theme.font.primary.weight.medium};
`

/* Mobile roundels per the Figma mobile hero — smaller circles with shorter
   Maldives copy, sitting over the card-in-sand area below the content. */
const WinBadgeMobile = styled.div`
  position: absolute;
  /* Anchored to the viewport centre, which is where the card sits below desktop
     once the artwork is cropped to a tall frame. The offsets hang the roundels
     off the card's lower-left corner in the same proportion as the desktop
     placement — roughly a sixth of the card's width below its bottom edge, and a
     fourteenth of it inside the leading edge. The layer above is cropped to 124%
     rather than filling the frame specifically to leave room for that overhang. */
  bottom: ${rem(13)};
  inset-inline-start: calc(50% - ${rem(120)});
  display: flex;
  align-items: flex-start;
  z-index: 1;

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const WinBadgeMobileMaldivesCircle = styled(WinBadgeMaldivesCircle)`
  min-width: ${rem(100)};
  min-height: ${rem(100)};
  padding: ${rem(8)};
  font-size: ${rem(14)};
`

const WinBadgeMobilePointsCircle = styled(WinBadgePointsCircle)`
  margin-block-start: ${rem(28)};
  margin-inline-start: -${rem(16)};
  min-width: ${rem(108)};
  min-height: ${rem(108)};
  font-size: ${rem(14)};

  &:not(:lang(en)) {
    padding-inline-start: ${rem(22)};
  }
`

function LoyaltyCreditCardHeroSection() {
  const intl = useIntl()
  // LE employees get their own offer, so none of the public promotions
  // (prize draw, bonus points, welcome credit) are shown to them.
  const isStaff = useAppSelector(isLEStaff)

  return (
    <HeroWrapper>
      <HeroBackground>
        <Image
          id={LOYALTY_CREDIT_CARD_HERO_IMAGE_ID}
          sources={HERO_IMAGE_SOURCES}
          alt=""
          objectFit="cover"
          quality="best"
          width={2048}
          dpr={2}
        />
      </HeroBackground>
      {!isStaff && (
        <WinBadge>
          <WinBadgeMaldivesCircle>
            <span>
              {intl.formatMessage(
                {
                  id: '5OtVTPHJbe3azV1BFUvk',
                  defaultMessage: 'Your chance<lineBreak></lineBreak>to WIN<lineBreak></lineBreak>a trip to the<lineBreak></lineBreak><medium>Maldives</medium>³',
                  description: 'Maldives prize roundel overlaid on the credit card image in the Société Credit Card hero',
                },
                {
                  medium: (chunks: ReactNode) => <WinBadgeEmphasis key="win-badge-maldives-emphasis">{chunks}</WinBadgeEmphasis>,
                  lineBreak: () => <br key="win-badge-maldives-break" />,
                },
              )}
            </span>
          </WinBadgeMaldivesCircle>
          <WinBadgePointsCircle>
            <span>
              {intl.formatMessage(
                {
                  id: '8WrNArDlhTF4wMw2iS7a',
                  defaultMessage: 'and get<lineBreak></lineBreak><medium>25,000</medium><lineBreak></lineBreak>bonus points⁴',
                  description: 'Bonus points roundel overlaid on the credit card image in the Société Credit Card hero',
                },
                {
                  medium: (chunks: ReactNode) => <WinBadgeEmphasis key="win-badge-points-emphasis">{chunks}</WinBadgeEmphasis>,
                  lineBreak: () => <br key="win-badge-points-break" />,
                },
              )}
            </span>
          </WinBadgePointsCircle>
        </WinBadge>
      )}
      {!isStaff && (
        <WinBadgeMobile>
          <WinBadgeMobileMaldivesCircle>
            <span>
              {intl.formatMessage(
                {
                  id: 'dr9m6pl1bPiqVO_ePFJZ',
                  defaultMessage: 'WIN<lineBreak></lineBreak>a trip to the<lineBreak></lineBreak><medium>Maldives</medium>³',
                  description: 'Maldives prize roundel with shortened copy shown on mobile over the credit card image in the Société Credit Card hero',
                },
                {
                  medium: (chunks: ReactNode) => <WinBadgeEmphasis key="win-badge-maldives-emphasis-mobile">{chunks}</WinBadgeEmphasis>,
                  lineBreak: () => <br key="win-badge-maldives-break-mobile" />,
                },
              )}
            </span>
          </WinBadgeMobileMaldivesCircle>
          <WinBadgeMobilePointsCircle>
            <span>
              {intl.formatMessage(
                {
                  id: '8WrNArDlhTF4wMw2iS7a',
                  defaultMessage: 'and get<lineBreak></lineBreak><medium>25,000</medium><lineBreak></lineBreak>bonus points⁴',
                  description: 'Bonus points roundel overlaid on the credit card image in the Société Credit Card hero',
                },
                {
                  medium: (chunks: ReactNode) => <WinBadgeEmphasis key="win-badge-points-emphasis-mobile">{chunks}</WinBadgeEmphasis>,
                  lineBreak: () => <br key="win-badge-points-break-mobile" />,
                },
              )}
            </span>
          </WinBadgeMobilePointsCircle>
        </WinBadgeMobile>
      )}
      <LoyaltyCreditCardNav />
      <HeroContent>
        <LayoutContainer className={LOYALTY_CREDIT_CARD_LAYOUT_CONTAINER_CLASS} variant="medium">
          <HeroLayout>
            <HeroContentInner>
              <IntroducingBadge>
                {intl.formatMessage({
                  id: 'TKbrbmHOuQcFtdZuTmDe',
                  defaultMessage: 'Introducing the NEW Société Credit Card',
                  description: 'Hero badge on the Société Credit Card landing page',
                })}
              </IntroducingBadge>
              <Heading variant="heading1" align="center">
                {intl.formatMessage(
                  {
                    id: 'YsL1Uz1MXIKJJ9QIIVXu',
                    defaultMessage: '<italic>Points</italic> that take<lineBreak></lineBreak>you places',
                    description: 'Hero headline on the Société Credit Card landing page',
                  },
                  {
                    italic: (chunks: ReactNode) => <em key="hero-heading-emphasis">{chunks}</em>,
                    lineBreak: () => <br key="hero-heading-line-break" />,
                  },
                )}
              </Heading>
              <BulletList>
                <BulletItem>
                  <BulletIcon aria-hidden="true">
                    <LineCheckCircleIcon size="S" />
                  </BulletIcon>
                  {intl.formatMessage({
                    id: 'pGRqQxTz0x7gjLdmt-ZB',
                    defaultMessage: 'Earn 2 Société Points per $1 spent¹',
                    description: 'First bullet point in the hero section on the Société Credit Card page',
                  })}
                </BulletItem>
                {!isStaff && (
                  <BulletItem>
                    <BulletIcon aria-hidden="true">
                      <LineTicketIcon size="S" />
                    </BulletIcon>
                    {intl.formatMessage({
                      id: '2zRvYrRSXghBljBlGpTO',
                      defaultMessage: '$150 Luxury Escapes credit²',
                      description: 'Second bullet point in the hero section on the Société Credit Card page',
                    })}
                  </BulletItem>
                )}
                <BulletItem>
                  <BulletIcon aria-hidden="true">
                    <LineClockWithTickIcon size="S" />
                  </BulletIcon>
                  {intl.formatMessage({
                    id: 'NRH7nMri3Q0Ii-Hn5r2S',
                    defaultMessage: 'Ready in minutes. Available on your Phone Digital Wallet.',
                    description: 'Third bullet point in the hero section on the Société Credit Card page',
                  })}
                </BulletItem>
              </BulletList>
              <HeroApplyButton placement={LOYALTY_CREDIT_CARD_APPLY_NOW_PLACEMENTS.hero} />
            </HeroContentInner>
            <HeroCardColumn />
          </HeroLayout>
        </LayoutContainer>
      </HeroContent>
    </HeroWrapper>
  )
}

export default LoyaltyCreditCardHeroSection
